import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Trash2, Globe, Book, Music, Shield, X } from 'lucide-react';
interface SettingsScreenProps {
  onClose: () => void;
}
export function SettingsScreen({
  onClose
}: SettingsScreenProps) {
  return <motion.div initial={{
    x: '100%'
  }} animate={{
    x: 0
  }} exit={{
    x: '100%'
  }} transition={{
    type: 'spring',
    damping: 25,
    stiffness: 200
  }} className="fixed inset-0 bg-gradient-to-b from-white to-sky-50 z-50 overflow-y-auto" data-id="element-161">
      {/* Header */}
      <div className="sticky top-0 bg-white/80 backdrop-blur-sm border-b border-slate-200 px-6 py-4 flex items-center justify-between" data-id="element-162">
        <h2 className="text-xl font-bold text-slate-800" data-id="element-163">Settings</h2>
        <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100 transition-colors focus:outline-none focus:ring-2 focus:ring-sky-400" aria-label="Close settings" data-id="element-164">
          <X className="w-5 h-5 text-slate-600" data-id="element-165" />
        </button>
      </div>

      <div className="px-6 py-6 max-w-md mx-auto space-y-6" data-id="element-166">
        {/* Bible Translation */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100" data-id="element-167">
          <div className="flex items-center gap-3 mb-4" data-id="element-168">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-400 rounded-full flex items-center justify-center" data-id="element-169">
              <Book className="w-5 h-5 text-white" data-id="element-170" />
            </div>
            <div data-id="element-171">
              <h3 className="font-semibold text-slate-800" data-id="element-172">
                Bible Translation
              </h3>
              <p className="text-sm text-slate-600" data-id="element-173">
                Choose your preferred version
              </p>
            </div>
          </div>
          <select className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-400/20 text-slate-800" data-id="element-174">
            <option data-id="element-175">New International Version (NIV)</option>
            <option data-id="element-176">King James Version (KJV)</option>
            <option data-id="element-177">English Standard Version (ESV)</option>
            <option data-id="element-178">New Revised Standard Version (NRSV)</option>
            <option data-id="element-179">New Living Translation (NLT)</option>
            <option data-id="element-180">The Message (MSG)</option>
          </select>
        </div>

        {/* Music Style */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100" data-id="element-181">
          <div className="flex items-center gap-3 mb-4" data-id="element-182">
            <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full flex items-center justify-center" data-id="element-183">
              <Music className="w-5 h-5 text-white" data-id="element-184" />
            </div>
            <div data-id="element-185">
              <h3 className="font-semibold text-slate-800" data-id="element-186">
                Music Preferences
              </h3>
              <p className="text-sm text-slate-600" data-id="element-187">
                Select your worship style
              </p>
            </div>
          </div>
          <div className="space-y-2" data-id="element-188">
            {['Gospel', 'Contemporary Worship', 'Traditional Hymns', 'Christian Pop', 'Instrumental'].map(style => <label key={style} className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 cursor-pointer" data-id="element-189">
                <input type="checkbox" defaultChecked={style === 'Contemporary Worship'} className="w-5 h-5 text-sky-400 border-slate-300 rounded focus:ring-2 focus:ring-sky-400" data-id="element-190" />
                <span className="text-slate-700" data-id="element-191">{style}</span>
              </label>)}
          </div>
        </div>

        {/* Language */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100" data-id="element-192">
          <button className="w-full flex items-center justify-between" data-id="element-193">
            <div className="flex items-center gap-3" data-id="element-194">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-indigo-400 rounded-full flex items-center justify-center" data-id="element-195">
                <Globe className="w-5 h-5 text-white" data-id="element-196" />
              </div>
              <div className="text-left" data-id="element-197">
                <h3 className="font-semibold text-slate-800" data-id="element-198">Language</h3>
                <p className="text-sm text-slate-600" data-id="element-199">English (US)</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" data-id="element-200" />
          </button>
        </div>

        {/* Privacy & Data */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100" data-id="element-201">
          <div className="flex items-center gap-3 mb-4" data-id="element-202">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center" data-id="element-203">
              <Shield className="w-5 h-5 text-white" data-id="element-204" />
            </div>
            <div data-id="element-205">
              <h3 className="font-semibold text-slate-800" data-id="element-206">Privacy & Data</h3>
              <p className="text-sm text-slate-600" data-id="element-207">Manage your information</p>
            </div>
          </div>
          <div className="space-y-3" data-id="element-208">
            <button className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 text-left" data-id="element-209">
              <span className="text-slate-700" data-id="element-210">Privacy Policy</span>
              <ChevronRight className="w-5 h-5 text-slate-400" data-id="element-211" />
            </button>
            <button className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 text-left" data-id="element-212">
              <span className="text-slate-700" data-id="element-213">Terms of Service</span>
              <ChevronRight className="w-5 h-5 text-slate-400" data-id="element-214" />
            </button>
            <button className="w-full flex items-center gap-2 justify-center p-3 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors font-medium" data-id="element-215">
              <Trash2 className="w-4 h-4" data-id="element-216" />
              <span data-id="element-217">Delete My History</span>
            </button>
          </div>
        </div>

        {/* App Info */}
        <div className="text-center text-sm text-slate-500 pt-4" data-id="element-218">
          <p data-id="element-219">SoulLift v1.0.0</p>
          <p className="mt-1" data-id="element-220">
            Made with 💙 for the global Christian community
          </p>
        </div>
      </div>
    </motion.div>;
}